import { z } from 'zod';
import queryString from 'query-string';
import { DEFAULT_PAGE_LIMIT } from '$lib/stores/Careers/jobsStore';
import { captureMessage, withScope, setContext } from '@sentry/sveltekit';

const JobsListRequestSchema = z.object({
  page: z.coerce.number().default(1),
  pageLimit: z.coerce.number().default(() => DEFAULT_PAGE_LIMIT),
  query: z.coerce.string().optional(),
  teams: z.array(z.string()).optional(),
  locations: z.array(z.string()).optional(),
  employeeTypes: z.array(z.string()).optional(),
  isRemote: z.coerce.boolean().optional(),
  businessUnits: z.array(z.string()).optional()
});

export type JobsListRequest = z.infer<typeof JobsListRequestSchema>;

export function jobsListRequestToQueryString(data: JobsListRequest) {
  // Because there is no skipBoolean option,
  // we remove isRemote=false to avoid polluting the URL
  if (data.isRemote === false) {
    delete data.isRemote;
  }
  return queryString.stringify(data, {
    arrayFormat: 'bracket',
    skipEmptyString: true
  });
}

export function partialJobsListRequestToQueryString(data: Partial<JobsListRequest>) {
  // Because there is no skipBoolean option,
  // we remove isRemote=false to avoid polluting the URL
  if (data.isRemote === false) {
    delete data.isRemote;
  }
  return queryString.stringify(data, {
    arrayFormat: 'bracket',
    skipEmptyString: true
  });
}

export function jobsListRequestFromQueryString(url: URL) {
  const parsedQueryString = queryString.parse(url.search, {
    arrayFormat: 'bracket',
    parseNumbers: true,
    parseBooleans: true
  });

  const safeParsedFilterObject = JobsListRequestSchema.safeParse(parsedQueryString);

  if (safeParsedFilterObject.success) {
    return safeParsedFilterObject.data;
  } else {
    withScope((scope) => {
      scope.setContext('Query String Value', { value: url.search });
      captureMessage('Invalid query string value', 'info');
    });

    return {
      page: 1,
      pageLimit: DEFAULT_PAGE_LIMIT
    };
  }
}
